import { createApi } from "@reduxjs/toolkit/query/react";
import { customBaseQuery, handleQueryLifecycle } from "store/utils";

export interface MutualFundEmailData {
  id: string;
  email: string;
  mfBroker: string;
  createdAt: string;
  updatedAt: string;
  password: string | null;
  setUpEmail: string | null;
  branch: number;
}

export interface BrokerConfigureResponse {
  id: number;
  createdOn: string;
  updatedOn: string;
  brokerName: string;
  appName: string;
  appSource: string;
  userId: string;
  password: string;
  userKey: string;
  encryptionKey: string;
  ocpKey: string;
  partnerCode: string;
  partnerPassword: string;
  partnerDob: string;
  partnerEmailId: string;
  partnerContactNumber: string;
  company: number;
}

export interface CommonResponse<T> {
  data: T;
  message: string;
  status: number;
}

type ID = string | number;

export const settingsApi = createApi({
  reducerPath: "settingsApi",
  baseQuery: customBaseQuery,
  tagTypes: ["MutualFundEmail", "BrokerConfigure"],
  endpoints: (builder) => ({
    getMutualFundEmail: builder.query<
      CommonResponse<MutualFundEmailData>,
      void
    >({
      query: () => ({
        url: `/advisor-auto-forward/`,
        method: "GET",
      }),
      providesTags: () => [{ type: "MutualFundEmail" }],
    }),

    updateAdvisorAutoForward: builder.mutation<
      CommonResponse<MutualFundEmailData>,
      Partial<MutualFundEmailData>
    >({
      query: (body) => ({
        url: "/advisor-auto-forward/",
        method: "POST",
        body,
      }),
      invalidatesTags: (result) =>
        result ? [{ type: "MutualFundEmail" }] : [],
      onQueryStarted: async (_, { queryFulfilled }) =>
        handleQueryLifecycle(queryFulfilled),
    }),

    confirmMutualFundAutoForward: builder.mutation<any, string>({
      query: (id) => ({
        url: `/advisor-auto-forward/${id}/confirm/`,
        method: "POST",
      }),
      invalidatesTags: (result) =>
        result ? [{ type: "MutualFundEmail" }] : [],
      onQueryStarted: async (_, { queryFulfilled }) =>
        handleQueryLifecycle(queryFulfilled),
    }),

    getBrokerDetails: builder.query<
      CommonResponse<BrokerConfigureResponse>,
      ID
    >({
      query: (id) => ({
        url: `/company/${id}/broker_details/`,
        method: "GET",
      }),
      providesTags: () => [{ type: "BrokerConfigure" }],
    }),

    updateBrokerDetails: builder.mutation<
      CommonResponse<BrokerConfigureResponse>,
      { id: ID; body: Partial<BrokerConfigureResponse> }
    >({
      query: ({ id, body }) => ({
        url: `/company/${id}/broker_details/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (result) =>
        result ? [{ type: "BrokerConfigure" }] : [],
      onQueryStarted: async (_, { queryFulfilled }) =>
        handleQueryLifecycle(queryFulfilled),
    }),
  }),
});

export const {
  useGetMutualFundEmailQuery,
  useUpdateAdvisorAutoForwardMutation,
  useConfirmMutualFundAutoForwardMutation,
  useGetBrokerDetailsQuery,
  useUpdateBrokerDetailsMutation,
} = settingsApi;
